import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import Modal from './modal'
import { getDocumentDetails, importDocumentItems } from '../../redux/actions/documentAction'
import { docDownloadUrl, docViewUrl } from '../../config/config'
import { formatDate, formatMoney, getErrorMessage } from '../../utils'

export const METHOD_LABELS = {
    'excel-direct': 'Read directly from spreadsheet',
    'word-direct': 'Read directly from Word table',
    'excel-text+ai': 'Spreadsheet text structured by AI',
    'word-text+ai': 'Word text structured by AI',
    'pdf-text+ai': 'PDF text structured by AI',
    'pdf-vision+ai': 'Scanned PDF read by AI vision',
    'image-vision+ai': 'Image read by AI vision',
    'text+ai': 'Text structured by AI',
}

const PREVIEW_IMAGE = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
const EMPTY_ROW = { name: '', companyName: '', quantity: '', price: '', amount: '', confidence: 'high', uncertainFields: [], note: '' }
const EMPTY_VENDOR = { companyName: '', address: '', invoiceDate: '', mobile: '' }

let rowKey = 0
const withKey = (row) => ({ ...EMPTY_ROW, ...row, _key: ++rowKey, selected: true, error: '' })

const blank = (v) => (v === null || v === undefined ? '' : v)

function mathMismatch(row) {
    const [q, p, a] = [row.quantity, row.price, row.amount].map(v => (v === '' || v === null ? null : Number(v)))
    if ([q, p, a].some(v => v === null || Number.isNaN(v))) return false
    return Math.abs(q * p - a) > Math.max(0.5, Math.abs(a) * 0.01)
}

// The original file, shown next to the extracted items for checking
const DocumentFrame = ({ doc }) => {
    if (doc.mimeType === 'application/pdf') {
        return <iframe className="DocFrame" src={docViewUrl(doc._id)} title={doc.originalName} />
    }
    if (PREVIEW_IMAGE.includes(doc.mimeType)) {
        return <div className="DocFrame DocFrameImage"><img src={docViewUrl(doc._id)} alt={doc.originalName} /></div>
    }
    return (
        <div className="DocFrame DocFrameEmpty">
            <i className="fa fa-file-o"></i>
            <p>Preview is not available for this file type.</p>
            <a className="Button" href={docDownloadUrl(doc._id)}><i className="fa fa-download"></i> Download to view</a>
        </div>
    )
}

const ScanReview = ({ docId, onClose, onImported }) => {
    const dispatch = useDispatch()
    const [doc, setDoc] = useState(null)
    const [rows, setRows] = useState([])
    const [vendor, setVendor] = useState(EMPTY_VENDOR)
    const [uncertainDetails, setUncertainDetails] = useState([])
    const [saving, setSaving] = useState(false)
    const [expanded, setExpanded] = useState(false)
    const [showOriginal, setShowOriginal] = useState(true)

    useEffect(() => {
        if (!docId) return
        setDoc(null)
        setExpanded(false)
        dispatch(getDocumentDetails(docId))
            .then(res => {
                const ex = res.data.extraction || {}
                setDoc(res.data)
                setRows((ex.items || []).map(withKey))
                setVendor({ companyName: ex.companyName || '', address: ex.address || '', invoiceDate: ex.invoiceDate || '', mobile: ex.mobile || '' })
                setUncertainDetails(ex.uncertainDetails || [])
            })
            .catch(err => {
                toast.error(getErrorMessage(err))
                onClose()
            })
    }, [docId, dispatch, onClose])

    const update = (key, field, value) => {
        setRows(prev => prev.map(r => (r._key === key
            ? { ...r, [field]: value, error: '', uncertainFields: r.uncertainFields.filter(f => f !== field) }
            : r)))
    }

    const updateVendor = (field, value) => {
        setVendor(prev => ({ ...prev, [field]: value }))
        setUncertainDetails(prev => prev.filter(f => f !== field))
    }

    const selected = rows.filter(r => r.selected)
    const allSelected = rows.length > 0 && selected.length === rows.length

    const handleSave = async () => {
        if (!selected.length) return toast.error('Select at least one item to save')
        const missingName = selected.filter(r => !String(r.name).trim())
        if (missingName.length) {
            setRows(prev => prev.map(r => (r.selected && !String(r.name).trim() ? { ...r, error: 'Item name is required' } : r)))
            return toast.error(`${missingName.length} selected row(s) need an item name`)
        }

        setSaving(true)
        const markFailed = (failed) => new Map(failed.map(f => [selected[f.index]._key, f.message]))
        try {
            const payload = selected.map(({ name, companyName, quantity, price }) => ({ name, companyName, quantity, price }))
            const res = await dispatch(importDocumentItems(docId, { items: payload, ...vendor }))
            const failed = res.data.failed || []
            if (!failed.length) {
                toast.success(res.message)
                onImported()
                onClose()
                return
            }
            // Keep only the rows that failed, with their reason, so nothing is silently lost
            const failedByKey = markFailed(failed)
            setRows(prev => prev.filter(r => !r.selected || failedByKey.has(r._key)).map(r => ({ ...r, error: failedByKey.get(r._key) || r.error })))
            toast.warn(res.message)
            onImported()
        } catch (err) {
            const failed = err?.response?.data?.data?.failed
            if (failed) {
                const failedByKey = markFailed(failed)
                setRows(prev => prev.map(r => ({ ...r, error: failedByKey.get(r._key) || r.error })))
            }
            toast.error(getErrorMessage(err))
        } finally {
            setSaving(false)
        }
    }

    const ex = doc?.extraction || {}
    const fieldClass = (row, field) => `form-control form-control-sm${row.uncertainFields.includes(field) ? ' is-invalid' : ''}`
    const vendorClass = (field) => `form-control form-control-sm${uncertainDetails.includes(field) ? ' is-invalid' : ''}`

    return (
        <Modal show={!!docId} onClose={() => !saving && onClose()} size={expanded ? 'modal-xl ModalExpanded' : 'modal-xl'}>
            {!doc ? <p className="text-center py-4">Loading scanned items...</p> : (
                <div className="ScanReview">
                    <div className="ReviewHeader">
                        <h3>Review Scanned Items</h3>
                        <div className="ReviewTools">
                            <button type="button" className={`Button Cancel ${showOriginal ? 'Active' : ''}`} onClick={() => setShowOriginal(v => !v)}>
                                <i className={`fa ${showOriginal ? 'fa-eye-slash' : 'fa-eye'}`}></i> {showOriginal ? 'Hide original' : 'Show original'}
                            </button>
                            <button type="button" className="Button Cancel" onClick={() => setExpanded(v => !v)}>
                                <i className={`fa ${expanded ? 'fa-compress' : 'fa-expand'}`}></i> {expanded ? 'Exit full screen' : 'Expand'}
                            </button>
                        </div>
                    </div>
                    <p className="ScanMeta">
                        <b>{doc.originalName}</b> · {METHOD_LABELS[ex.method] || ex.method}{ex.model ? ` (${ex.model})` : ''}
                        {' · '}{rows.length} row(s){Number.isInteger(ex.expectedRowCount) ? `, ${ex.expectedRowCount} counted in document` : ''}
                    </p>

                    <div className={`ReviewBody ${showOriginal ? 'WithOriginal' : ''}`}>
                        {showOriginal && (
                            <div className="ReviewOriginal">
                                <DocumentFrame doc={doc} />
                            </div>
                        )}

                        <div className="ReviewData">
                            {ex.importedCount > 0 && (
                                <div className="alert alert-info py-2">
                                    {ex.importedCount} item(s) from this document were already saved on {formatDate(ex.importedAt)}. Saving again creates new items.
                                </div>
                            )}
                            {ex.warnings?.length > 0 && (
                                <div className="alert alert-warning py-2">
                                    <ul className="mb-0 pl-3">{ex.warnings.map(w => <li key={w}>{w}</li>)}</ul>
                                </div>
                            )}

                            <fieldset className="VendorBox">
                                <legend>Vendor Details <small>(saved on every item)</small></legend>
                                <div className="row">
                                    <div className="col-md-6 form-group">
                                        <label>Company Name</label>
                                        <input className={vendorClass('companyName')} value={vendor.companyName} onChange={e => updateVendor('companyName', e.target.value)} placeholder="Vendor name or invoice title" />
                                    </div>
                                    <div className="col-md-3 form-group">
                                        <label>Mobile Number</label>
                                        <input type="tel" className={vendorClass('mobile')} value={vendor.mobile} onChange={e => updateVendor('mobile', e.target.value)} />
                                    </div>
                                    <div className="col-md-3 form-group">
                                        <label>Invoice Date</label>
                                        <input type="date" className={vendorClass('invoiceDate')} value={vendor.invoiceDate} onChange={e => updateVendor('invoiceDate', e.target.value)} />
                                    </div>
                                    <div className="col-12 form-group mb-0">
                                        <label>Address</label>
                                        <textarea rows="2" className={vendorClass('address')} value={vendor.address} onChange={e => updateVendor('address', e.target.value)} />
                                    </div>
                                </div>
                            </fieldset>

                            <div className="d-flex justify-content-between align-items-center my-2">
                                <b>Items</b>
                                <button type="button" className="Button Cancel" onClick={() => setRows(prev => [...prev, withKey({})])}>
                                    <i className="fa fa-plus"></i> Add missing row
                                </button>
                            </div>

                            <div className="TableList ReviewTable">
                                <table>
                                    <thead>
                                        <tr>
                                            <th><input type="checkbox" checked={allSelected} onChange={e => setRows(prev => prev.map(r => ({ ...r, selected: e.target.checked })))} /></th>
                                            <th>#</th>
                                            <th>Item Name</th>
                                            <th>Company</th>
                                            <th>Qty</th>
                                            <th>Price</th>
                                            <th>Amount</th>
                                            <th>Check</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, i) => (
                                            <tr key={row._key} className={`${row.confidence === 'low' ? 'RowLow' : ''} ${row.selected ? '' : 'RowOff'}`}>
                                                <td><input type="checkbox" checked={row.selected} onChange={e => update(row._key, 'selected', e.target.checked)} /></td>
                                                <td>{i + 1}</td>
                                                <td>
                                                    <input className={fieldClass(row, 'name')} value={row.name} onChange={e => update(row._key, 'name', e.target.value)} />
                                                    {row.error && <small className="text-danger d-block text-left">{row.error}</small>}
                                                </td>
                                                <td><input className={fieldClass(row, 'companyName')} value={row.companyName} placeholder={vendor.companyName} onChange={e => update(row._key, 'companyName', e.target.value)} /></td>
                                                <td><input type="number" className={fieldClass(row, 'quantity')} value={blank(row.quantity)} onChange={e => update(row._key, 'quantity', e.target.value)} /></td>
                                                <td><input type="number" step="0.01" className={fieldClass(row, 'price')} value={blank(row.price)} onChange={e => update(row._key, 'price', e.target.value)} /></td>
                                                <td><input type="number" step="0.01" className={fieldClass(row, 'amount')} value={blank(row.amount)} onChange={e => update(row._key, 'amount', e.target.value)} /></td>
                                                <td className="text-left">
                                                    <span className={`Conf Conf-${row.confidence}`}>{row.confidence}</span>
                                                    {mathMismatch(row) && <small className="d-block text-danger">Qty × Price = {formatMoney(Number(row.quantity) * Number(row.price))}</small>}
                                                    {row.note && <small className="d-block text-muted">{row.note}</small>}
                                                </td>
                                                <td>
                                                    <button type="button" className="IconBtn Red" title="Remove row" onClick={() => setRows(prev => prev.filter(r => r._key !== row._key))}>
                                                        <i className="fa fa-times"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {!rows.length && <tr><td colSpan="9">No items were found. Use "Add missing row" to enter them.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="text-right mt-3">
                        <button type="button" className="Button Cancel mr-2" onClick={onClose} disabled={saving}>Close</button>
                        <button type="button" className="Button" onClick={handleSave} disabled={saving || !selected.length}>
                            {saving ? 'Saving...' : `Save ${selected.length} item${selected.length === 1 ? '' : 's'} to Item Manager`}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    )
}

export default ScanReview
